import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Loader2 } from "lucide-react";
import { useGoogleConnection } from "@/contexts/GoogleConnectionContext";
import {
  clearGoogleOAuthPending,
  consumeGoogleOAuthRedirect,
  fetchTokenInfo,
  fetchUserProfile,
  googleOAuthRedirectUri,
  googleOAuthReturnPath,
  saveToken,
  tokenHasGmailScope,
} from "@/lib/googleAuth";
import { probeGmailAccess } from "@/services/gmailService";

export default function GoogleOAuthCallback() {
  const [, setLocation] = useLocation();
  const { applySession } = useGoogleConnection();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const parsed = consumeGoogleOAuthRedirect();
      if (!parsed) {
        setError("No Google sign-in result found. Close this tab and try Connect Gmail again.");
        return;
      }
      if (!parsed.ok) {
        clearGoogleOAuthPending();
        setError(parsed.error);
        return;
      }
      try {
        const profile = await fetchUserProfile(parsed.accessToken);
        saveToken(parsed.accessToken, profile);
        const info = await fetchTokenInfo(parsed.accessToken);
        if (tokenHasGmailScope(info)) {
          try {
            await probeGmailAccess(parsed.accessToken);
          } catch {
            /* Inbox pages surface Gmail API errors after return. */
          }
        }
        if (cancelled) return;
        await applySession(parsed.accessToken, profile);
        clearGoogleOAuthPending();
        setLocation(googleOAuthReturnPath());
      } catch (e) {
        if (!cancelled) {
          clearGoogleOAuthPending();
          setError(e instanceof Error ? e.message : "Google sign-in failed after redirect.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession, setLocation]);

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-6 text-center">
      {error ? (
        <>
          <p className="max-w-lg text-sm text-destructive">{error}</p>
          <p className="max-w-lg text-xs text-muted-foreground">
            If Google showed redirect_uri_mismatch, add{" "}
            <code className="break-all">{googleOAuthRedirectUri()}</code> as an Authorized
            redirect URI on the OAuth client (origin only, no extra path). Production:{" "}
            <code className="break-all">https://www.deeplysmart.app</code>.
          </p>
        </>
      ) : (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Finishing Google sign-in…</p>
        </>
      )}
    </div>
  );
}
